import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  SignOut, 
  Users, 
  ChartLineUp, 
  Table, 
  CaretRight,
  Eye,
  Clock,
  EnvelopeSimple,
  Phone,
  Buildings,
  MagnifyingGlass,
  Funnel
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { API_URL, formatCurrency } from '../../lib/utils';

export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, new: 0, today: 0 });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchLeads();
    }
  }, [user]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/leads`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
        
        // Calculate stats
        const today = new Date().toISOString().split('T')[0];
        const todayLeads = data.filter(l => l.created_at.startsWith(today));
        const newLeads = data.filter(l => l.status === 'new');
        
        setStats({
          total: data.length,
          new: newLeads.length,
          today: todayLeads.length
        });
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await fetch(`${API_URL}/leads/${leadId}/status?status=${status}`, {
        method: 'PATCH',
        credentials: 'include',
      });
      fetchLeads();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const filteredLeads = leads.filter(lead => {
    const matchesFilter = filter === 'all' || lead.source === filter || lead.status === filter;
    const matchesSearch = search === '' || 
      lead.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      lead.email?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0048ff] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Header */}
      <header className="bg-[#0A1128] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#0048ff] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">M</span>
                </div>
                <span className="font-semibold">Mentor Health</span>
              </Link>
              <span className="text-white/40">|</span>
              <span className="text-white/60">Admin Panel</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-white/60 text-sm">{user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/60 hover:text-white" data-testid="admin-logout-btn">
                <SignOut size={18} className="mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div 
            className="mentor-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#0048ff]/10 flex items-center justify-center">
                <Users size={24} className="text-[#0048ff]" weight="duotone" />
              </div>
              <div>
                <p className="text-sm text-[#11111f]/60">Total Leads</p>
                <p className="text-3xl font-semibold text-[#11111f]">{stats.total}</p>
              </div>
            </div>
          </motion.div>
          <motion.div 
            className="mentor-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <ChartLineUp size={24} className="text-emerald-500" weight="duotone" />
              </div>
              <div>
                <p className="text-sm text-[#11111f]/60">New Leads</p>
                <p className="text-3xl font-semibold text-emerald-500">{stats.new}</p>
              </div>
            </div>
          </motion.div>
          <motion.div 
            className="mentor-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#0048ff]/10 flex items-center justify-center">
                <Clock size={24} className="text-[#0048ff]" weight="duotone" />
              </div>
              <div>
                <p className="text-sm text-[#11111f]/60">Today's Leads</p>
                <p className="text-3xl font-semibold text-[#0048ff]">{stats.today}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Link to="/admin/rates">
            <div className="mentor-card flex items-center justify-between group">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#0048ff]/10 flex items-center justify-center">
                  <Table size={20} className="text-[#0048ff]" />
                </div>
                <div>
                  <p className="font-semibold text-[#11111f]">Manage Rate Tables</p>
                  <p className="text-sm text-[#11111f]/60">Edit insurance premium rates</p>
                </div>
              </div>
              <CaretRight size={20} className="text-[#11111f]/40 group-hover:text-[#0048ff] transition-colors" />
            </div>
          </Link>
          <Link to="/">
            <div className="mentor-card flex items-center justify-between group">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Eye size={20} className="text-emerald-500" />
                </div>
                <div>
                  <p className="font-semibold text-[#11111f]">View Website</p>
                  <p className="text-sm text-[#11111f]/60">See the public website</p>
                </div>
              </div>
              <CaretRight size={20} className="text-[#11111f]/40 group-hover:text-emerald-500 transition-colors" />
            </div>
          </Link>
        </div>

        {/* Leads Table */}
        <div className="mentor-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-xl font-semibold text-[#11111f]">Leads</h2>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#11111f]/40" />
                <Input
                  placeholder="Search leads..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 w-full md:w-64"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-full md:w-40">
                  <Funnel size={16} className="mr-2" />
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leads</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="register">Registration</SelectItem>
                  <SelectItem value="demo">Demo Request</SelectItem>
                  <SelectItem value="insurance_calculator">Insurance Calculator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#0048ff] border-t-transparent rounded-full mx-auto" />
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="py-12 text-center text-[#11111f]/60">
              No leads found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-[#11111f]/10">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#11111f]/60">Company</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#11111f]/60">Contact</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#11111f]/60">Source</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#11111f]/60">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#11111f]/60">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#11111f]/60">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="border-b border-[#11111f]/5 hover:bg-[#F8F9FB] transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#0048ff]/10 flex items-center justify-center">
                            <Buildings size={18} className="text-[#0048ff]" />
                          </div>
                          <div>
                            <p className="font-medium text-[#11111f]">{lead.company_name}</p>
                            {lead.employees && <p className="text-xs text-[#11111f]/60">{lead.employees} employees</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="space-y-1">
                          {lead.contact_name && <p className="text-sm text-[#11111f]">{lead.contact_name}</p>}
                          <div className="flex items-center gap-1 text-xs text-[#11111f]/60">
                            <EnvelopeSimple size={12} />
                            {lead.email}
                          </div>
                          {lead.phone && (
                            <div className="flex items-center gap-1 text-xs text-[#11111f]/60">
                              <Phone size={12} />
                              {lead.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lead.source === 'register' ? 'bg-[#0048ff]/10 text-[#0048ff]' :
                          lead.source === 'demo' ? 'bg-[#0048ff]/10 text-[#0048ff]' :
                          lead.source === 'insurance_calculator' ? 'bg-emerald-500/10 text-emerald-500' :
                          'bg-[#11111f]/10 text-[#11111f]/60'
                        }`}>
                          {lead.source}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Select value={lead.status} onValueChange={(v) => updateLeadStatus(lead.id, v)}>
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="qualified">Qualified</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                            <SelectItem value="lost">Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-4 px-4 text-sm text-[#11111f]/60">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <Button variant="ghost" size="sm">
                          <Eye size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
